import { FontSize } from "config/font";
import { useEffect, useRef, useState } from "react";
import {
  DropdownArrow,
  DropdownContainer,
  DropdownItem,
  DropdownLabel,
  DropdownList,
  DropdownSelect,
  PlaceholderText,
  SelectedValue,
} from "./dropDownWithLabel.styled";
import { EyeIcon } from "../textInput/textInput.styled";
import { Eye } from "../textInput/textInput.component";

export interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownWithLabelProps {
  label: string;
  options: DropdownOption[];
  value?: string;
  required?: boolean;
  hasError?: boolean;
  fontSize?: FontSize;
  placeholder?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  inputSize?: "small" | "medium";
  width?: number;
  marginLeft?: number;
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
}

export const DropdownWithLabel: React.FC<DropdownWithLabelProps> = (props) => {
  const {
    label,
    options,
    value,
    required,
    hasError,
    fontSize,
    placeholder,
    disabled,
    onChange,
    onBlur,
    onFocus,
    inputSize = "medium",
    width,
    marginLeft,
    marginTop,
    marginRight,
    marginBottom,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const hasValue = Boolean(value && value.length > 0);
  const selectedOption = options.find((o) => o.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsFocused(false);
        onBlur?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onBlur]);

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) {
      setIsFocused(true);
      onFocus?.();
    }
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (selectedValue: string) => {
    if (disabled) return;
    onChange?.(selectedValue);
    setIsOpen(false);
    setIsFocused(false);
    onBlur?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleToggle();
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setIsFocused(false);
      onBlur?.();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        setIsFocused(true);
        onFocus?.();
      }
    }
  };

  return (
    <DropdownContainer
      ref={dropdownRef}
      className="drop-down-with-label"
      $size={inputSize}
      width={width}
      marginBottom={marginBottom}
      marginLeft={marginLeft}
      marginRight={marginRight}
      marginTop={marginTop}
    >
      <DropdownLabel
        required={required}
        hasValue={hasValue}
        isFocused={isFocused || isOpen}
        disabled={disabled}
        $size={inputSize}
      >
        {label}
      </DropdownLabel>

      <DropdownSelect
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        hasError={hasError}
        fontSize={fontSize}
        hasValue={hasValue}
        isOpen={isOpen}
        disabled={disabled}
        onClick={handleToggle}
        $size={inputSize}
      >
        {hasValue ? (
          <SelectedValue $size={inputSize}>
            {selectedOption?.label}
          </SelectedValue>
        ) : (
          <PlaceholderText $size={inputSize}>{placeholder}</PlaceholderText>
        )}

        {disabled ? (
          <EyeIcon>
            <Eye size={inputSize === "small" ? 12 : 16} />
          </EyeIcon>
        ) : (
          <DropdownArrow isOpen={isOpen} $size={inputSize} />
        )}
      </DropdownSelect>

      {!disabled && (
        <DropdownList isOpen={isOpen} role="listbox">
          {options.map((option) => (
            <DropdownItem
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              onClick={() => handleSelect(option.value)}
              $size={inputSize}
            >
              {option.label}
            </DropdownItem>
          ))}
        </DropdownList>
      )}
    </DropdownContainer>
  );
};
